import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowLeft, LifeBuoy, Loader2, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2,
  Clock, BadgeEuro, Search, FileText, MessageSquare, Truck, Package, Handshake, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import ShipmentTimeline from '../components/ShipmentTimeline';
import {
  CASE_STATUS_META, CASE_TYPE_META, PRIORITY_META, PROPOSAL_TYPE_META, REFUND_STATUS_LABELS,
  RETURN_STATUS_LABELS, TIMELINE_META, adminCaseApi, formatDate, formatMoney, isOpenCase,
} from '../lib/orderCases';

const STATUS_FILTERS = [
  { key: '', label: 'Tutti' },
  { key: 'open', label: 'Aperti' },
  { key: 'WAITING_FOR_SELLER', label: 'In attesa artigiano' },
  { key: 'WAITING_FOR_BUYER', label: 'In attesa acquirente' },
  { key: 'IN_DISCUSSION', label: 'In discussione' },
  { key: 'WAITING_FOR_ACCEPTANCE', label: 'Proposta pendente' },
  { key: 'ESCALATED_TO_RARIFEX', label: 'Escalati' },
  { key: 'overdue', label: 'Scaduti' },
  { key: 'RESOLVED', label: 'Risolti' },
];

const StatCard = ({ icon: Icon, label, value, cls, index }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
    <Card className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xl font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </Card>
  </motion.div>
);

/* --------------------------- Dettaglio caso --------------------------- */
const CaseDetail = ({ caseId, actorId, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [adminMessage, setAdminMessage] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await adminCaseApi.detail(caseId));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.message);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#008080]" /></div>;
  }
  if (!data) return null;

  const typeMeta = CASE_TYPE_META[data.type] || CASE_TYPE_META.OTHER;
  const statusMeta = CASE_STATUS_META[data.status] || {};

  return (
    <div className="space-y-6" data-testid="admin-case-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="admin-case-back">
        <ArrowLeft className="w-4 h-4" /> Tutti i casi
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl ${typeMeta.bg} ring-1 ${typeMeta.ring} flex items-center justify-center`}>
            <typeMeta.Icon className={`w-6 h-6 ${typeMeta.accent}`} />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">{typeMeta.label}</h2>
            <p className="text-sm text-slate-500">
              <span className="font-mono">{data.case_number}</span> · Ordine{' '}
              <span className="font-mono">{data.order_number}</span> · {formatDate(data.created_at, true)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className={`${statusMeta.cls} border`}>{statusMeta.label || data.status}</Badge>
          <Badge className={PRIORITY_META[data.priority]?.cls}>{PRIORITY_META[data.priority]?.label}</Badge>
          {data.return_status !== 'NOT_APPLICABLE' && (
            <Badge className="bg-teal-50 text-teal-700 border-teal-200 border">{RETURN_STATUS_LABELS[data.return_status]}</Badge>
          )}
          {data.refund_status !== 'NONE' && (
            <Badge className="bg-amber-50 text-amber-800 border-amber-200 border">{REFUND_STATUS_LABELS[data.refund_status]}</Badge>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#008080]" /> Caso</h3>
            {data.reason_code && <Badge className="bg-slate-100 text-slate-700 mb-2">{data.reason_code}</Badge>}
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{data.description}</p>
            {data.external_video_url && (
              <a href={data.external_video_url} target="_blank" rel="noreferrer" className="text-sm text-[#008080] hover:underline block mt-3">
                Video allegato →
              </a>
            )}
            {data.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {data.attachments.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                    {a.mime_type?.startsWith('image/') ? (
                      <img src={a.url} alt={a.file_name} className="w-20 h-20 rounded-lg object-cover ring-1 ring-slate-200 hover:ring-[#008080]/50" />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center ring-1 ring-slate-200">
                        <FileText className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
              <p>Acquirente: <span className="text-slate-800">{data.buyer_name}</span></p>
              <p>Artigiano: <span className="text-slate-800">{data.seller_name}</span></p>
              <p>Scadenza artigiano: <span className="text-slate-800">{formatDate(data.seller_deadline, true)}</span></p>
              <p>Scadenza acquirente: <span className="text-slate-800">{formatDate(data.buyer_deadline, true)}</span></p>
              <p>Tipo fornitura: <span className="text-slate-800">{data.fulfillment_type}</span></p>
              <p>Spedizione all'apertura: <span className="text-slate-800">{data.shipment_status_at_open || '—'}</span></p>
            </div>
            {data.escalation_reason && (
              <div className="mt-4 rounded-xl bg-fuchsia-50 border border-fuchsia-100 p-3">
                <p className="text-xs font-semibold text-fuchsia-800 mb-1">Motivo dell'escalation</p>
                <p className="text-sm text-fuchsia-900">{data.escalation_reason}</p>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#008080]" /> Conversazione</h3>
            <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
              {(data.thread || []).map((m) => (
                <div key={m.id} className={`rounded-xl px-3 py-2 text-sm ${
                  m.message_type === 'system_case' ? 'bg-[#008080]/6 border border-[#008080]/15 text-slate-700' : 'bg-slate-50 text-slate-700'
                }`}>
                  <p className="whitespace-pre-line">{m.content.replace(/\*\*/g, '')}</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {m.message_type === 'system_case' ? 'Rarifex' : m.sender_name} · {formatDate(m.created_at, true)}
                  </p>
                </div>
              ))}
              {!data.thread?.length && <p className="text-sm text-slate-400">Nessun messaggio.</p>}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Handshake className="w-4 h-4 text-[#008080]" /> Proposte</h3>
            <div className="space-y-2">
              {(data.proposals || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {PROPOSAL_TYPE_META[p.type]?.label} {p.refund_amount ? `· ${formatMoney(p.refund_amount)}` : ''}
                    </p>
                    <p className="text-xs text-slate-500">{p.proposer_name} · {formatDate(p.created_at, true)}</p>
                  </div>
                  <Badge className="bg-white border border-slate-200 text-slate-600">{p.status}</Badge>
                </div>
              ))}
              {!data.proposals?.length && <p className="text-sm text-slate-400">Nessuna proposta.</p>}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><BadgeEuro className="w-4 h-4 text-[#008080]" /> Rimborsi</h3>
            <div className="space-y-2">
              {(data.refunds || []).map((r) => (
                <div key={r.id} className="p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800">{formatMoney(r.amount)}</p>
                    <Badge className={
                      r.status === 'SUCCEEDED' ? 'bg-emerald-100 text-emerald-700'
                      : r.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                    }>{r.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{r.reason || '—'} · {formatDate(r.created_at, true)}</p>
                  <p className="text-[11px] font-mono text-slate-400 mt-1">
                    {r.stripe_refund_reference || '—'} / {r.stripe_charge_reference || '—'} / {r.stripe_connected_account || '—'}
                  </p>
                  {r.failure_reason && <p className="text-xs text-red-600 mt-1">{r.failure_reason}</p>}
                </div>
              ))}
              {!data.refunds?.length && <p className="text-sm text-slate-400">Nessun rimborso.</p>}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#008080]" /> Timeline completa</h3>
            <div className="space-y-3">
              {(data.timeline || []).map((t) => {
                const meta = TIMELINE_META[t.action] || { label: t.action, Icon: ShieldCheck, cls: 'text-slate-500 bg-slate-100' };
                return (
                  <div key={t.id} className="flex gap-3">
                    <div className={`w-7 h-7 rounded-full ${meta.cls} flex items-center justify-center flex-shrink-0`}>
                      <meta.Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-xs">
                      <p className="text-sm text-slate-700">{meta.label}{t.is_internal ? ' (interno)' : ''}</p>
                      <p className="text-slate-400">
                        {formatDate(t.created_at, true)} · {t.actor_type}
                        {t.previous_status ? ` · ${t.previous_status} → ${t.new_status}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Azioni Superadmin */}
        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Gestione</h3>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Priorità</Label>
                <Select
                  value={data.priority}
                  onValueChange={(v) => run(() => adminCaseApi.update(caseId, { actor_id: actorId, priority: v }), 'Priorità aggiornata')}
                >
                  <SelectTrigger data-testid="admin-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Stato</Label>
                <Select
                  value={data.status}
                  onValueChange={(v) => run(() => adminCaseApi.update(caseId, { actor_id: actorId, status: v }), 'Stato aggiornato')}
                >
                  <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CASE_STATUS_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Messaggio alle parti</Label>
                <Textarea
                  rows={3}
                  value={adminMessage}
                  onChange={(e) => setAdminMessage(e.target.value)}
                  placeholder="Scrivi come Rarifex…"
                  data-testid="admin-message-input"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 bg-[#008080] hover:bg-[#006666] text-white"
                    disabled={busy || !adminMessage.trim()}
                    onClick={() => run(async () => {
                      await adminCaseApi.message(caseId, { actor_id: actorId, content: adminMessage.trim() });
                      setAdminMessage('');
                    }, 'Messaggio inviato')}
                    data-testid="admin-send-message-btn"
                  >
                    Invia
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    disabled={busy}
                    onClick={() => run(async () => {
                      await adminCaseApi.message(caseId, {
                        actor_id: actorId,
                        content: adminMessage.trim() || 'Per procedere abbiamo bisogno di ulteriori informazioni e prove sul problema segnalato.',
                        request_evidence: true,
                      });
                      setAdminMessage('');
                    }, 'Prove richieste')}
                    data-testid="admin-request-evidence-btn"
                  >
                    Chiedi prove
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-slate-100">
                <Label className="text-xs">Rimborso manuale (max {formatMoney(data.refundable_amount)})</Label>
                <div className="flex gap-2">
                  <Input
                    type="number" min="0" step="0.01" max={data.refundable_amount}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0,00"
                    data-testid="admin-refund-input"
                  />
                  <Button
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    disabled={busy || !refundAmount}
                    onClick={() => run(async () => {
                      const r = await adminCaseApi.refund(caseId, {
                        requested_by: actorId, requested_by_role: 'SUPERADMIN',
                        amount: parseFloat(refundAmount), reason: 'Rimborso autorizzato da Rarifex',
                      });
                      setRefundAmount('');
                      if (r?.status === 'FAILED') {
                        throw new Error(`Rimborso non riuscito: ${r.failure_reason || 'errore Stripe'}`);
                      }
                    }, 'Rimborso completato')}
                    data-testid="admin-refund-btn"
                  >
                    Rimborsa
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-slate-100">
                <Label className="text-xs flex items-center gap-1.5"><Lock className="w-3 h-3" /> Nota interna (non visibile alle parti)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input" />
                <Button
                  size="sm" variant="outline" className="w-full"
                  disabled={busy || !note.trim()}
                  onClick={() => run(async () => {
                    await adminCaseApi.update(caseId, { actor_id: actorId, internal_note: note.trim() });
                    setNote('');
                  }, 'Nota salvata')}
                  data-testid="admin-note-btn"
                >
                  Aggiungi nota
                </Button>
                {data.internal_notes && (
                  <pre className="text-[11px] text-slate-500 whitespace-pre-wrap bg-slate-50 rounded-lg p-2 mt-2 max-h-32 overflow-y-auto">
                    {data.internal_notes}
                  </pre>
                )}
              </div>

              {!isOpenCase(data.status) && (
                <Button
                  variant="outline" className="w-full" disabled={busy}
                  onClick={() => run(() => adminCaseApi.reopen(caseId, actorId), 'Caso riaperto')}
                  data-testid="admin-reopen-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Riapri caso
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-[#008080]" /> Ordine</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Totale</span><span className="font-semibold">{formatMoney(data.order_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Rimborsato</span><span>{formatMoney(data.refunded_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Rimborsabile</span><span className="font-semibold text-[#008080]">{formatMoney(data.refundable_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Pagamento</span><span className="font-mono text-[11px]">{data.order?.stripe_session_id || '—'}</span></div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Truck className="w-4 h-4 text-[#008080]" /> Spedizioni</h3>
            <ShipmentTimeline orderId={data.order_id} compact />
            <Link to="/admin/shipments" className="text-xs text-[#008080] hover:underline mt-3 inline-block">
              Apri gestione spedizioni →
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Lista casi ----------------------------- */
export default function AdminOrderCasesPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        adminCaseApi.list({ status: statusFilter, case_type: typeFilter, priority: priorityFilter }),
        adminCaseApi.stats(),
      ]);
      setCases(list || []);
      setStats(s);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = cases.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [c.case_number, c.order_number, c.buyer_name, c.seller_name, c.description]
      .some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-order-cases-page">
      <Helmet><title>Assistenza ordini · Rarifex Admin</title></Helmet>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#008080] to-[#00CED1] flex items-center justify-center shadow-lg shadow-[#008080]/20">
              <LifeBuoy className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Assistenza ordini</h1>
              <p className="text-sm text-slate-500">Resi, problemi, accordi e rimborsi di tutta la piattaforma</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-cases-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Aggiorna
            </Button>
            <Button
              size="sm" className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={async () => {
                try {
                  const r = await adminCaseApi.checkDeadlines();
                  toast.success(`Scadenze verificate: ${r.reminders_sent} reminder, ${r.marked_overdue} scaduti`);
                  load();
                } catch (e) { toast.error(e.message); }
              }}
              data-testid="admin-check-deadlines"
            >
              <Clock className="w-4 h-4 mr-1.5" /> Verifica scadenze
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {selected ? (
          <CaseDetail caseId={selected} actorId={user?.id} onBack={() => setSelected(null)} onChanged={load} />
        ) : (
          <>
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <StatCard index={0} icon={LifeBuoy} label="Casi totali" value={stats.total} cls="bg-slate-100 text-slate-600" />
                <StatCard index={1} icon={Clock} label="Aperti" value={stats.open} cls="bg-amber-100 text-amber-700" />
                <StatCard index={2} icon={ShieldCheck} label="Escalati" value={stats.escalated} cls="bg-fuchsia-100 text-fuchsia-700" />
                <StatCard index={3} icon={AlertTriangle} label="Scaduti" value={stats.overdue} cls="bg-orange-100 text-orange-700" />
                <StatCard index={4} icon={CheckCircle2} label="Risolti" value={stats.resolved} cls="bg-emerald-100 text-emerald-700" />
                <StatCard index={5} icon={BadgeEuro} label="Rimborsato" value={formatMoney(stats.refunded_total)} cls="bg-teal-100 text-teal-700" />
              </div>
            )}

            <Card className="p-4 mb-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key || 'all'}
                    onClick={() => setStatusFilter(f.key)}
                    data-testid={`admin-filter-${f.key || 'all'}`}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                      statusFilter === f.key
                        ? 'bg-[#008080] text-white shadow-md shadow-[#008080]/20'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    className="pl-9"
                    placeholder="Cerca caso, ordine, utente…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="admin-cases-search"
                  />
                </div>
                <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="admin-type-filter"><SelectValue placeholder="Tipo di caso" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    {Object.entries(CASE_TYPE_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="admin-priority-filter"><SelectValue placeholder="Priorità" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le priorità</SelectItem>
                    {Object.entries(PRIORITY_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#008080]" /></div>
            ) : filtered.length === 0 ? (
              <Card className="p-12 text-center border-2 border-dashed">
                <p className="text-slate-500">Nessun caso con questi filtri.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {filtered.map((c, i) => {
                  const meta = CASE_TYPE_META[c.type] || CASE_TYPE_META.OTHER;
                  const status = CASE_STATUS_META[c.status] || {};
                  return (
                    <motion.button
                      key={c.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.03, 0.3) }}
                      onClick={() => setSelected(c.id)}
                      className="w-full text-left"
                      data-testid={`admin-case-row-${c.id}`}
                    >
                      <Card className={`p-4 hover:shadow-md transition-all ${c.is_overdue ? 'border-orange-200 bg-orange-50/40' : ''}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                            <meta.Icon className={`w-5 h-5 ${meta.accent}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-xs text-slate-400">{c.case_number}</span>
                              <span className="font-semibold text-sm text-slate-800">{meta.short}</span>
                              <Badge className={`${status.cls} border text-xs`}>{status.label || c.status}</Badge>
                              <Badge className={`${PRIORITY_META[c.priority]?.cls} text-xs`}>{PRIORITY_META[c.priority]?.label}</Badge>
                              {c.is_overdue && <Badge className="bg-orange-100 text-orange-700 text-xs">Scaduto</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 truncate">
                              {c.buyer_name} → {c.seller_name} · Ordine {c.order_number || '—'} · {c.shipment_status || 'nessuna spedizione'}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-semibold text-slate-800">{formatMoney(c.order_amount)}</p>
                            {c.refund_total > 0 && (
                              <p className="text-xs text-amber-700">rimborsato {formatMoney(c.refund_total)}</p>
                            )}
                            <p className="text-[11px] text-slate-400">{formatDate(c.updated_at, true)}</p>
                          </div>
                        </div>
                      </Card>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
